import type { OnboardingProgress, PortalMode } from './dicts'
import { TERMINAL_MODES } from './dicts'

/**
 * ⭐ 门禁模型 —— 全项目最容易做错的一处，先把口径说清楚。
 *
 * 用户中心有**两个正交维度**，必须叠加生效，缺一个都会放行不该放行的操作：
 *
 *   ① portal_mode + 开户进度 → 能进哪些页面（本文件）
 *   ② MT5 账户状态           → 对某个账户能否出入金（dicts.ts 的 MT5_ABILITY）
 *
 * 举例：NORMAL 模式、开户已完成的客户，若当前账户是 FROZEN（禁止交易），
 * 菜单进得去，但出金要拦。两个维度分别判定，不要合成一个 boolean。
 *
 * 「能力标签」（capability）直接沿用接口文档里每个端点标注的那个名字，
 * 前端的页面门禁与后端的 portal 闸用同一套词，联调时对得上（R-10 双重防护）。
 */
export const CAPABILITIES = [
  'security',      // 改密 / 登出：任何状态都可用
  'overview',      // 首页与开户进度：全模式可读（横幅与拦截弹窗的数据源）
  'onboarding',    // 续填开户入口（页面本体在开户篇，本篇只有入口）
  'activation',    // 知识测试 + 身份认证
  'profile.read',  // 个人资料：全模式可读，终态只读
  'profile.write', // 上传文件（接口 2.0 新增）
  'bank',          // 银行账号
  /**
   * 我的账户、平台下载，以及**资金全部功能**（入金 / 出金 / 汇率 / 记录）：
   * 仅已开通 MT5 的 NORMAL 客户。
   *
   * 接口 4.0 的 7 个 `/api/c/funding/**` 端点标的都是 `accounts`，
   * 所以这里**不再单设 `funding` / `reports` 占位标签** —— 那两个是接口没到位时
   * 前端自己起的名字，留着会让前端门禁与后端 portal 闸用两套词，
   * 违背 R-10 双重防护「同一套词」的前提。
   */
  'accounts',
  /**
   * 通知语言（接口 7.0 `POST /c/profile/locale`）：**全模式常开** —— 被拒户、冷却户照样在收我们的邮件，
   * 没道理不让改语言。所以下面每一格都有它，不参与任何维度的收窄。
   */
  'preference',
] as const
export type Capability = (typeof CAPABILITIES)[number]

/**
 * 能力矩阵 —— **来源是后端 `cfd-customer` 的 `PortalModePolicy.java`**（portal_mode + 进度 → 能力集的唯一映射点，
 * 网关粗闸与服务细闸都从它出），逐格照抄、不推导；它又是需求 §7「受限模式」那张表的落地。
 * 改一格先看后端那张 switch，评审时两边逐行核对。`src/constants/capabilities.test.ts` 把整张表对拍了一遍。
 *
 * 后端表（2026-10-10 核对）：
 *   BASE = overview · profile.read · security · preference（后端另有 address_lookup，本仓无此能力）
 *   NORMAL + PENDING_SUBMIT            → BASE + onboarding
 *   NORMAL + PENDING_ACCOUNT / PENDING_KYC → BASE + activation
 *   NORMAL + MT5_OPENED                → 全部，唯独没有 onboarding
 *   NORMAL + 其余进度（REJECTED / SUIT_FAILED_x / KT_FAILED_x / DUP_HOLD）→ BASE
 *   KT1 / KT2                          → BASE + activation
 *   SUIT1 / SUIT2 / AGE / KT3 / DUP    → BASE
 * `profile.write`（上传文件）只有 MT5_OPENED 这一格有：此前 PENDING_ACCOUNT / PENDING_KYC / KT1 / KT2 四格多放了它，
 * 真打 `POST /c/files` 会 40311。
 *
 * 注意 KT3 只出现在 portalMode，进度里没有 KT_FAILED_3（第 3 次失败直接落 REJECTED），
 * 所以判终态必须看 mode，不能看 progress。
 */
const MODE_CAPS: Record<PortalMode, Capability[]> = {
  // 正常模式的可用能力还要再按开户进度细分，见 capsOf()
  NORMAL: ['security', 'overview', 'profile.read', 'profile.write', 'onboarding', 'activation', 'bank', 'accounts', 'preference'],

  // 知识测试冷却中：菜单同「待开户」，激活入口可见但进场会被 42212 拦
  KT1: ['security', 'overview', 'profile.read', 'activation', 'preference'],
  KT2: ['security', 'overview', 'profile.read', 'activation', 'preference'],

  // 适应性失败 1 次：只读 + 冷却期满可重新申请
  SUIT1: ['security', 'overview', 'profile.read', 'preference'],

  // 终态：个人资料只读，无任何重试入口，仅保留改密与通知语言
  KT3: ['security', 'overview', 'profile.read', 'preference'],
  SUIT2: ['security', 'overview', 'profile.read', 'preference'],
  AGE: ['security', 'overview', 'profile.read', 'preference'],
  /**
   * 查重核实中（DUP_HOLD）：接口 0.13.5（cfd-platform 决策 023，2026-10-26）起**可以登录**——登录准入不再回 under_review，
   * 客户进来是 BASE：看得到首页横幅「申请正在人工审核中」与只读资料，等客服核实放行后进度照常往下走。
   * 0.10.0～0.13.4 这一格在登录准入那层就被拒、不可达（前端 /review-pending 独立页仍保留兼容旧后端）。
   */
  DUP: ['security', 'overview', 'profile.read', 'preference'],
}

/**
 * NORMAL 模式下再按开户进度收窄 —— §6.1 强制把「未完成开户」拆成两种：
 *
 *   待提交 PENDING_SUBMIT   申请表还没提交，激活任务**尚不存在**。
 *                           R-17：不得提供任何进入测试与认证的入口，含深链。
 *   待开户 PENDING_ACCOUNT  已提交且自动批核通过，激活任务已生成。
 *
 * 这两种共用一个 portal_mode（都是 NORMAL），只能靠 progress 区分，
 * 所以门禁不能只看 mode。
 */
const PROGRESS_CAPS: Partial<Record<OnboardingProgress, Capability[]>> = {
  PENDING_SUBMIT: ['security', 'overview', 'profile.read', 'onboarding', 'preference'],
  PENDING_ACCOUNT: ['security', 'overview', 'profile.read', 'activation', 'preference'],
  // KYC 转人工审核中：认证页还要能看「审核中」，但资金相关一律不给
  PENDING_KYC: ['security', 'overview', 'profile.read', 'activation', 'preference'],
  /**
   * 已开户也要有 `activation`：证件临期 / 过期的**重新认证**走的就是激活组端点
   * （compliance-alerts 的动作 KYC_RECHECK = `POST /activation/kyc/token`，契约明写已通过者在 docState=EXPIRING / EXPIRED 时放行）。
   * 此前这一行没有它，首页横幅「立即认证」与个人资料「重新认证」点进 `/activation/kyc/start` 被路由守卫拦回，
   * 弹出的却是「该功能当前不可用」。知识测试页随之也可达，它自己会显示「已通过」态，无副作用。
   */
  MT5_OPENED: ['security', 'overview', 'profile.read', 'profile.write', 'activation', 'bank', 'accounts', 'preference'],
}

/**
 * NORMAL 下进度表里没列的那些（REJECTED / SUIT_FAILED_x / KT_FAILED_x / DUP_HOLD）后端只给 BASE。
 * 以前这里回落成 MODE_CAPS.NORMAL 的全集 —— 「进度 REJECTED、mode 仍 NORMAL」的 KYC 驳回客户（mock `kyc-rejected@zr.au`）
 * 会被当成正常客户放进资金页，再被后端 40311 踢回来。现在与后端同格； 问的「mode 该是什么」仍待后端答复，不影响这一格。
 */
const BASE_CAPS: Capability[] = ['security', 'overview', 'profile.read', 'preference']

export function capsOf(progress: OnboardingProgress, mode: PortalMode): Capability[] {
  const byMode = MODE_CAPS[mode] ?? MODE_CAPS.NORMAL
  // 只有 NORMAL 再按进度细分；其余 mode 后端不看进度
  if (mode !== 'NORMAL') return byMode
  const byProgress = PROGRESS_CAPS[progress] ?? BASE_CAPS
  // 两个维度取交集：任一维度不允许就不允许
  return byMode.filter((c) => byProgress.includes(c))
}

export function isTerminal(mode: PortalMode): boolean {
  return TERMINAL_MODES.includes(mode)
}

/**
 * 拦截弹窗分流（§6.1 强制，不得共用同一个弹窗）：
 *   待提交 → 「请先完成开户申请」引导弹窗（6.1.1）
 *   待开户 → 「完成开户的最后步骤」任务弹窗（6.1.2）
 *   受限 / 终态 → 说明弹窗（仅「联系客服 + 确定」）
 */
export type BlockKind = 'apply' | 'tasks' | 'restricted' | 'none'

export function blockKindOf(progress: OnboardingProgress, mode: PortalMode): BlockKind {
  if (progress === 'MT5_OPENED') return 'none'
  if (mode !== 'NORMAL') return 'restricted'
  if (progress === 'PENDING_SUBMIT') return 'apply'
  return 'tasks'
}
